import type { MouseEvent, ReactNode } from 'react';
import type React from 'react';

import type { ButtonProps } from '@coursera/cds-core';

export const DEFFAULT_IMAGE_SIZE = { width: 172, height: 240 };
export const DEFAULT_LOGO_SIZE = { width: 83, height: 34 };
export const aspectRatio = DEFFAULT_IMAGE_SIZE.width / DEFFAULT_IMAGE_SIZE.height;

export const bannerHeight = 240;
export const pixelMarginBetweenAds = 24;
export const PLACEHOLDER_LINE_HEIGHT = 24;

export const BACKGROUND_COLORS = {
  dodgerBlue: 'dodgerBlue',
  froly: 'froly',
  white: 'white',
  goldenPoppy: 'goldenPoppy',
  lightCyan: 'lightCyan',
} as const;

export type BackgroundColor = typeof BACKGROUND_COLORS[keyof typeof BACKGROUND_COLORS];

export const BANNER_AD_PLACEMENTS = {
  ARTICLE: 'ARTICLE',
  BROWSE_DOMAIN: 'BROWSE_DOMAIN',
  BROWSE_HOME: 'BROWSE_HOME',
  BROWSE_SUBDOMAIN: 'BROWSE_SUBDOMAIN',
  LOGGED_IN_DASHBOARD: 'LOGGED_IN_DASHBOARD',
  MEGAMENU: 'MEGAMENU',
  WEEKLY_RECS_EMAIL: 'WEEKLY_RECS_EMAIL',
} as const;

export const BANNER_AD_COUNTS_BY_PLACEMENT = {
  [BANNER_AD_PLACEMENTS.LOGGED_IN_DASHBOARD]: 2,
  [BANNER_AD_PLACEMENTS.MEGAMENU]: 2,
  [BANNER_AD_PLACEMENTS.ARTICLE]: 2,
  [BANNER_AD_PLACEMENTS.BROWSE_HOME]: 2,
  [BANNER_AD_PLACEMENTS.BROWSE_DOMAIN]: 2,
  [BANNER_AD_PLACEMENTS.BROWSE_SUBDOMAIN]: 2,
  [BANNER_AD_PLACEMENTS.WEEKLY_RECS_EMAIL]: 2,
} as const;

export type BannerAdPlacement = typeof BANNER_AD_PLACEMENTS[keyof typeof BANNER_AD_PLACEMENTS];

export type BannerAdViewProps = {
  backgroundColor: BackgroundColor;
  bodyText: string | ReactNode;
  callToActionText?: string;
  imageUrl: string;
  imageAltText?: string;
  logoUrl?: string;
  logoAltText?: string;
  shouldDisplayImageResponsively?: boolean;
  imageWidthInPixels?: number;
  partnerName?: string | JSX.Element | null;
  titleText: string;
  forceOpenInNewTab?: boolean;
};

export type BannerAdCreative = BannerAdViewProps & {
  creativeSlug: string;
};

export type BannerAdCampaign = {
  id: string;
  internalName: string;
  callToActionUrl: string;
  creative: BannerAdCreative;
  placement: BannerAdPlacement; // the intended display place as computed by the BE
  promotedEntityId: string | null; // for eventing
};

export type BannerAdProps = BannerAdViewProps & {
  className?: string;
  heightInPixels?: number;
  trackingName?: string;
  callToActionUrl: string;
  trackingData?: { promotedEntityId?: string | null; placement?: BannerAdPlacement };
  // Style prop is for overriding size with Storybook
  style?: React.CSSProperties;
  ctaButtonType?: ButtonProps['variant'];
  leftSideClassNameOverride?: string;
  /**
   * If present, will override opening the `callToActionUrl`. Tracking will still take place.
   */
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
};

export type BannerAdRowProps = {
  adData: Array<BannerAdProps>;
  className?: string;
  heightInPixels?: number;
  onRefUpdated?: (x0?: HTMLElement | null) => void;
  placement?: BannerAdPlacement;
  loading?: boolean;
};
